import { IApi } from '../../api'
import {
  ITwilioAssignParams,
  ITwilioListParams,
  ITwilioMessagesParams,
  ITwilioProvisionParams,
  ITwilioReleaseParams,
  ITwilioSearchParams,
  ITwilioSetUnsupportedParams,
} from '../../api/twilio'
import { createAction } from '../types'

export const prefix = 'twilio'

export const LIST_PHONE_NUMBERS_REQUEST = `${prefix}/LIST_PHONE_NUMBERS_REQUEST`
export const LIST_PHONE_NUMBERS_SUCCESS = `${prefix}/LIST_PHONE_NUMBERS_SUCCESS`
export const LIST_PHONE_NUMBERS_FAILURE = `${prefix}/LIST_PHONE_NUMBERS_FAILURE`

export const listPhoneNumbers = createAction(
  (params: ITwilioListParams & { skipLoader?: boolean } = {}) => ({
    type: [LIST_PHONE_NUMBERS_REQUEST, LIST_PHONE_NUMBERS_SUCCESS, LIST_PHONE_NUMBERS_FAILURE],
    promise: (api: IApi) => api.twilio.listPhoneNumbers(params),
    params,
  }),
)

export const LIST_NUMBER_OPTIONS_REQUEST = `${prefix}/LIST_NUMBER_OPTIONS_REQUEST`
export const LIST_NUMBER_OPTIONS_SUCCESS = `${prefix}/LIST_NUMBER_OPTIONS_SUCCESS`
export const LIST_NUMBER_OPTIONS_FAILURE = `${prefix}/LIST_NUMBER_OPTIONS_FAILURE`

/**
 * The whole pool, unfiltered, to populate the messages feed's Number dropdown.
 *
 * Its own action and slice rather than reading `phoneNumbers`: that bucket is
 * what the numbers table renders, so it carries the table's own filters and
 * page — the dropdown would then offer only the numbers the table happens to be
 * showing, and narrow further as ops filtered it.
 */
export const listNumberOptions = createAction((params: ITwilioListParams = {}) => ({
  type: [LIST_NUMBER_OPTIONS_REQUEST, LIST_NUMBER_OPTIONS_SUCCESS, LIST_NUMBER_OPTIONS_FAILURE],
  promise: (api: IApi) => api.twilio.listPhoneNumbers(params),
}))

export const SEARCH_AVAILABLE_NUMBERS_REQUEST = `${prefix}/SEARCH_AVAILABLE_NUMBERS_REQUEST`
export const SEARCH_AVAILABLE_NUMBERS_SUCCESS = `${prefix}/SEARCH_AVAILABLE_NUMBERS_SUCCESS`
export const SEARCH_AVAILABLE_NUMBERS_FAILURE = `${prefix}/SEARCH_AVAILABLE_NUMBERS_FAILURE`

export const searchAvailableNumbers = createAction((params: ITwilioSearchParams) => ({
  type: [
    SEARCH_AVAILABLE_NUMBERS_REQUEST,
    SEARCH_AVAILABLE_NUMBERS_SUCCESS,
    SEARCH_AVAILABLE_NUMBERS_FAILURE,
  ],
  promise: (api: IApi) => api.twilio.searchAvailableNumbers(params),
}))

export const PROVISION_NUMBER_REQUEST = `${prefix}/PROVISION_NUMBER_REQUEST`
export const PROVISION_NUMBER_SUCCESS = `${prefix}/PROVISION_NUMBER_SUCCESS`
export const PROVISION_NUMBER_FAILURE = `${prefix}/PROVISION_NUMBER_FAILURE`

export const provisionNumber = createAction((data: ITwilioProvisionParams) => ({
  type: [PROVISION_NUMBER_REQUEST, PROVISION_NUMBER_SUCCESS, PROVISION_NUMBER_FAILURE],
  promise: (api: IApi) => api.twilio.provisionNumber(data),
}))

export const ASSIGN_NUMBER_REQUEST = `${prefix}/ASSIGN_NUMBER_REQUEST`
export const ASSIGN_NUMBER_SUCCESS = `${prefix}/ASSIGN_NUMBER_SUCCESS`
export const ASSIGN_NUMBER_FAILURE = `${prefix}/ASSIGN_NUMBER_FAILURE`

export const assignNumber = createAction((phoneNumberId: string, data: ITwilioAssignParams) => ({
  type: [ASSIGN_NUMBER_REQUEST, ASSIGN_NUMBER_SUCCESS, ASSIGN_NUMBER_FAILURE],
  promise: (api: IApi) => api.twilio.assignNumber(phoneNumberId, data),
}))

export const RELEASE_NUMBER_REQUEST = `${prefix}/RELEASE_NUMBER_REQUEST`
export const RELEASE_NUMBER_SUCCESS = `${prefix}/RELEASE_NUMBER_SUCCESS`
export const RELEASE_NUMBER_FAILURE = `${prefix}/RELEASE_NUMBER_FAILURE`

export const releaseNumber = createAction(
  (phoneNumberId: string, data: ITwilioReleaseParams = {}) => ({
    type: [RELEASE_NUMBER_REQUEST, RELEASE_NUMBER_SUCCESS, RELEASE_NUMBER_FAILURE],
    promise: (api: IApi) => api.twilio.releaseNumber(phoneNumberId, data),
  }),
)

export const SET_UNSUPPORTED_REQUEST = `${prefix}/SET_UNSUPPORTED_REQUEST`
export const SET_UNSUPPORTED_SUCCESS = `${prefix}/SET_UNSUPPORTED_SUCCESS`
export const SET_UNSUPPORTED_FAILURE = `${prefix}/SET_UNSUPPORTED_FAILURE`

/** Flags (or clears) a number a customer portal rejected for being a VoIP line. */
export const setUnsupported = createAction(
  (phoneNumberId: string, data: ITwilioSetUnsupportedParams) => ({
    type: [SET_UNSUPPORTED_REQUEST, SET_UNSUPPORTED_SUCCESS, SET_UNSUPPORTED_FAILURE],
    promise: (api: IApi) => api.twilio.setUnsupported(phoneNumberId, data),
  }),
)

export const LIST_MESSAGES_REQUEST = `${prefix}/LIST_MESSAGES_REQUEST`
export const LIST_MESSAGES_SUCCESS = `${prefix}/LIST_MESSAGES_SUCCESS`
export const LIST_MESSAGES_FAILURE = `${prefix}/LIST_MESSAGES_FAILURE`

export const listMessages = createAction(
  (params: ITwilioMessagesParams & { skipLoader?: boolean } = {}) => ({
    type: [LIST_MESSAGES_REQUEST, LIST_MESSAGES_SUCCESS, LIST_MESSAGES_FAILURE],
    promise: (api: IApi) => api.twilio.listMessages(params),
    params,
  }),
)

export const LIST_ALL_MESSAGES_REQUEST = `${prefix}/LIST_ALL_MESSAGES_REQUEST`
export const LIST_ALL_MESSAGES_SUCCESS = `${prefix}/LIST_ALL_MESSAGES_SUCCESS`
export const LIST_ALL_MESSAGES_FAILURE = `${prefix}/LIST_ALL_MESSAGES_FAILURE`

/**
 * Every code received across the pool, for the list under the numbers table.
 *
 * Its own action and slice rather than sharing `listMessages`: that feed is
 * gated on the number whose modal is open, and the two would overwrite each
 * other whenever both are on screen.
 */
export const listAllMessages = createAction(
  (params: ITwilioMessagesParams & { skipLoader?: boolean } = {}) => ({
    type: [LIST_ALL_MESSAGES_REQUEST, LIST_ALL_MESSAGES_SUCCESS, LIST_ALL_MESSAGES_FAILURE],
    promise: (api: IApi) => api.twilio.listMessages(params),
    params,
  }),
)

export const GET_RECORDING_REQUEST = `${prefix}/GET_RECORDING_REQUEST`
export const GET_RECORDING_SUCCESS = `${prefix}/GET_RECORDING_SUCCESS`
export const GET_RECORDING_FAILURE = `${prefix}/GET_RECORDING_FAILURE`

/** Fetches the call audio as a blob; the feed plays it from an object URL. */
export const getRecording = createAction((messageId: string) => ({
  type: [GET_RECORDING_REQUEST, GET_RECORDING_SUCCESS, GET_RECORDING_FAILURE],
  promise: (api: IApi) => api.twilio.getRecording(messageId),
}))

export const CLEAR_MESSAGES = `${prefix}/CLEAR_MESSAGES`

/**
 * Drops the loaded feed when the messages modal closes.
 *
 * Two jobs. It stops one number's verification codes being rendered under the
 * next number opened (the reducer keeps `data` through a REQUEST and a FAILURE,
 * so without this the previous feed is what a failed fetch leaves on screen).
 * And it un-scopes the websocket saga: `lastMessagesParams` is what tells it
 * which feed is open, so leaving it set means every inbound code for the rest
 * of the session refetches a feed nobody is looking at.
 */
export const clearMessages = () => ({ type: CLEAR_MESSAGES })

export const CLEAR_ALL_MESSAGES = `${prefix}/CLEAR_ALL_MESSAGES`

/**
 * Drops the all-messages feed when the list unmounts, for the same reason
 * `clearMessages` drops the per-number one: `lastAllMessagesParams` is what
 * tells the websocket saga this feed is on screen, so leaving it set means
 * every inbound code for the rest of the session refetches a list nobody is
 * looking at.
 */
export const clearAllMessages = () => ({ type: CLEAR_ALL_MESSAGES })
